/**
 * default-rule-date-range — the date window a rule INHERITS when it has none of
 * its own: per the layers it applies to, the first applicable formula group's
 * window, else `last-7-days`. Mirrors the backend `rulesCubeLookupKey` /
 * `groupAppliesToLayer` (see cube-lookup-key.ts) so the grey hint matches what
 * the engine actually uses. Pure — recomputes as the formula changes.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { ALL_LAYERS, type FlowLayer, type FormulaGroup } from '@trafficpulse/shared';

import { getStatsDateRangeLabel } from '@/components/date-range';

const DEFAULT_RANGE = 'last-7-days';

export interface InheritedWindow {
  dateRange: string;
  from?: string;
  to?: string;
}

export interface RuleDefaultWindow {
  /** The inherited window + label for each layer the rule applies to. */
  perLayer: Array<{ layer: FlowLayer; label: string; window: InheritedWindow }>;
  /** A single label when every applicable layer agrees, else null (→ "varies"). */
  unifiedLabel: string | null;
  /** The window to pre-fill when the user clicks to set an explicit range. */
  seed: InheritedWindow;
}

/** Legacy empty/undefined `appliesTo` on a formula group == all layers. */
function appliesToLayer(group: FormulaGroup, layer: FlowLayer): boolean {
  return !group.appliesTo?.length || group.appliesTo.includes(layer);
}

function firstApplicableWindow(
  groups: FormulaGroup[],
  layer: FlowLayer,
): InheritedWindow {
  const g = groups.find((x) => appliesToLayer(x, layer));
  if (!g) return { dateRange: DEFAULT_RANGE };
  return {
    dateRange: g.dateRange,
    from: g.dateRangeCustomFrom,
    to: g.dateRangeCustomTo,
  };
}

export function defaultRuleDateRange(
  formulaGroups: FormulaGroup[],
  appliesTo: readonly FlowLayer[],
): RuleDefaultWindow {
  const layers = appliesTo.length ? [...appliesTo] : [...ALL_LAYERS];
  const perLayer = layers.map((layer) => {
    const window = firstApplicableWindow(formulaGroups, layer);
    return { layer, label: getStatsDateRangeLabel(window.dateRange), window };
  });
  const labels = new Set(perLayer.map((p) => p.label));
  return {
    perLayer,
    unifiedLabel: labels.size === 1 ? perLayer[0].label : null,
    seed: perLayer[0].window,
  };
}
